import { HelpCircle, AlertCircle, FileText, Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useUploadInvoiceValidationFileMutation,
  useRunInvoiceGroupValidationMutation,
  useDeleteInvoiceValidationFileMutation,
  useGetCsvValidateSourcesQuery,
} from 'src/redux/billing/billing.endpoints';
import { InvoiceGroupDetails, ValidationFile } from 'src/redux/billing/billing.types';
import StyledDropdown from 'src/components/styled-dropdown';
import { Button } from 'src/components/ui/button';
import {
  getValidationFileSource,
  validationFileKey,
} from '../../utils/validation-file-utils';

interface PendingFile {
  file: File | null;
  source: string;
}

interface ValidateUploadContainerProps {
  invoiceGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails | null;
  onUpload?: () => void;
  onValidate?: () => void;
}

const VALIDATION_SOURCE_LABELS: Record<string, string> = {
  google_ads: 'Google Ads',
  yahoo_dsp: 'Yahoo DSP',
  nexxen_dsp: 'Nexxen DSP',
  nexxen_dsp_2184: 'Nexxen DSP (seat 2184)',
};

const formatValidationSourceLabel = (source: string): string =>
  VALIDATION_SOURCE_LABELS[source] ?? source.replace(/_/g, ' ');

const FILE_TYPES = ['.csv'] as const;
const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const formatUploadedAt = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

interface ValidationFileChipProps {
  file: ValidationFile;
  isUploading?: boolean;
  isDeleting?: boolean;
  onRemove?: () => void;
}

const ValidationFileChip: React.FC<ValidationFileChipProps> = ({
  file,
  isUploading = false,
  isDeleting = false,
  onRemove,
}) => {
  const fileSource = getValidationFileSource(file);
  const isPending = isUploading || isDeleting;
  const titleParts = [
    fileSource ? `Source: ${fileSource}` : null,
    file.uploaded_at ? formatUploadedAt(file.uploaded_at) : null,
    isUploading ? 'Uploading…' : null,
    isDeleting ? 'Removing…' : null,
  ].filter(Boolean);

  return (
    <div
      className={`inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border py-0.5 pl-3 pr-0.5 text-xs shadow-sm transition-opacity ${
        isPending
          ? 'border-gray-300 bg-gray-50 text-gray-600'
          : 'border-gray-200 bg-white text-gray-800'
      }`}
      title={titleParts.length > 0 ? titleParts.join(' · ') : undefined}
    >
      {isPending ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-500" aria-hidden />
      ) : null}
      {fileSource ? (
        <span className="max-w-[100px] shrink-0 truncate rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
          {fileSource.replace(/_/g, ' ')}
        </span>
      ) : null}
      <span className={`max-w-[min(200px,65vw)] truncate ${isUploading ? 'text-gray-500' : ''}`}>
        {file.filename}
      </span>
      {isUploading ? (
        <span className="shrink-0 pr-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Uploading
        </span>
      ) : (
        <button
          type="button"
          className="inline-flex shrink-0 rounded-full p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-40 disabled:hover:bg-transparent"
          aria-label={`Remove ${file.filename}`}
          title={fileSource ? undefined : 'Source is required to remove this file'}
          disabled={isDeleting || !fileSource || !onRemove}
          onClick={onRemove}
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </div>
  );
};

const ValidateUploadContainer: React.FC<ValidateUploadContainerProps> = ({
  invoiceGroupId,
  invoiceGroupDetails,
  onUpload,
  onValidate,
}) => {
  const [pendingRows, setPendingRows] = useState<PendingFile[]>([{ file: null, source: '' }]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploadingRowIndex, setUploadingRowIndex] = useState<number | null>(null);
  const [uploadingFileKeys, setUploadingFileKeys] = useState<Set<string>>(() => new Set());
  const [deletingFileKeys, setDeletingFileKeys] = useState<Set<string>>(() => new Set());

  const { data: sources = [], isLoading: isLoadingSources } = useGetCsvValidateSourcesQuery();
  const sourceOptions = useMemo(
    () => sources.map((s) => ({ value: s, label: formatValidationSourceLabel(s) })),
    [sources],
  );
  const [uploadFile] = useUploadInvoiceValidationFileMutation();
  const [runValidation, { isLoading: isRunningValidation }] = useRunInvoiceGroupValidationMutation();
  const [deleteValidationFile] = useDeleteInvoiceValidationFileMutation();

  const validationFiles: ValidationFile[] = invoiceGroupDetails?.validations?.files ?? [];
  const validationErrors: string[] = (invoiceGroupDetails?.errors ?? []).map((e) => e.error);
  const hasUploadInProgress = uploadingFileKeys.size > 0;
  const hasUploadedFiles = validationFiles.length > 0;

  /** Keep the native file input in sync with pending state where the browser allows it. */
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    pendingRows.forEach((row, index) => {
      const input = fileInputRefs.current[index];
      if (!input) {
        return;
      }
      if (!row.file) {
        input.value = '';
        return;
      }
      try {
        const dt = new DataTransfer();
        dt.items.add(row.file);
        input.files = dt.files;
      } catch {
        // Some browsers restrict programmatic FileList assignment; filename still shown beside control.
      }
    });
  }, [pendingRows, fileInputKey]);

  const trackUploadingFile = (key: string) => {
    setUploadingFileKeys((prev) => new Set(prev).add(key));
  };

  const untrackUploadingFile = (key: string) => {
    setUploadingFileKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const trackDeletingFile = (key: string) => {
    setDeletingFileKeys((prev) => new Set(prev).add(key));
  };

  const untrackDeletingFile = (key: string) => {
    setDeletingFileKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const setRowFile = (index: number, file: File | null) => {
    setUploadError(null);
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], file };
      return next;
    });
  };

  const setRowSource = (index: number, source: string) => {
    setUploadError(null);
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        source,
        ...(source.trim() ? {} : { file: null as File | null }),
      };
      return next;
    });
    if (!source.trim()) {
      setFileInputKey((k) => k + 1);
    }
  };

  const onFileInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>, source: string) => {
    if (!source.trim()) {
      setUploadError('Select a source before choosing a file.');
      e.target.value = '';
      return;
    }
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!f) {
      setRowFile(index, null);
      return;
    }
    const name = f.name.toLowerCase();
    const isCsv = FILE_TYPES.some((type) => name.endsWith(type));
    if (!isCsv) {
      setUploadError(`Please select a ${FILE_TYPES.join(' or ')} file.`);
      setRowFile(index, null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setUploadError('File too large. Max 25 MB.');
      setRowFile(index, null);
      return;
    }
    setUploadError(null);
    setRowFile(index, f);
  };

  const uploadOne = async(index: number) => {
    const row = pendingRows[index];
    if (!row.file || !row.source) {
      setUploadError('Select a file and a source for this row.');
      return;
    }

    const fileToUpload = row.file;
    const sourceToUpload = row.source;
    const uploadKey = validationFileKey(sourceToUpload, fileToUpload.name);
    setUploadError(null);
    setUploadingRowIndex(index);
    trackUploadingFile(uploadKey);
    setRowFile(index, null);
    setRowSource(index, '');
    setFileInputKey((k) => k + 1);

    try {
      await uploadFile({
        file: fileToUpload,
        id: invoiceGroupId,
        source: sourceToUpload,
      }).unwrap();
      onUpload?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Upload failed.';
      setUploadError(message);
    } finally {
      untrackUploadingFile(uploadKey);
      setUploadingRowIndex((current) => (current === index ? null : current));
    }
  };

  const handleRunValidation = async() => {
    setUploadError(null);
    try {
      await runValidation(invoiceGroupId).unwrap();
      onValidate?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Validation failed.';
      setUploadError(message);
    }
  };

  const handleRemoveValidationFile = async(source: string, filename: string) => {
    const deleteKey = validationFileKey(source, filename);
    setUploadError(null);
    trackDeletingFile(deleteKey);
    try {
      await deleteValidationFile({ id: invoiceGroupId, source, filename }).unwrap();
      onUpload?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Could not remove file.';
      setUploadError(message);
    } finally {
      untrackDeletingFile(deleteKey);
    }
  };

  return (
    <div className="p-4 pt-2 bg-gray-201 rounded-b-lg">
      <div className="flex flex-row gap-2 items-start mb-3">
        <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600">
          Select a source, choose a CSV file, then click Upload. When you are ready, click Run validation.
        </p>
      </div>

      {/* Pending uploads: single file + source row; clears after upload */}
      <div className="space-y-2 mb-4">
        {pendingRows.map((row, index) => {
          const fileInputId = `validate-invoice-csv-${index}-${fileInputKey}`;
          const hasSource = Boolean(row.source?.trim());
          const hasFile = Boolean(row.file);
          const isRowUploading = uploadingRowIndex === index;
          const canUploadRow = hasSource && hasFile && !isRowUploading && !hasUploadInProgress;
          return (
            <div
              key={`${index}-${fileInputKey}`}
              className="flex flex-wrap items-center gap-2"
            >
              <StyledDropdown
                options={sourceOptions}
                selectedValue={row.source?.trim() ? row.source : undefined}
                onSelect={(value) => setRowSource(index, value)}
                placeholder="Source"
                buttonVariant="primary"
                isLoading={isLoadingSources}
                buttonClassName="rounded-full bg-white border border-gray-200 text-gray-700 h-8 min-w-[160px] text-sm justify-between px-3 py-0 font-normal hover:bg-gray-50"
                className="shrink-0"
              />
              <input
                id={fileInputId}
                key={fileInputKey}
                ref={(el) => {
                  fileInputRefs.current[index] = el;
                }}
                type="file"
                accept=".csv,text/csv"
                disabled={!hasSource || isRowUploading}
                className="sr-only"
                onChange={(e) => onFileInputChange(index, e, row.source)}
              />
              <label
                htmlFor={hasSource && !isRowUploading ? fileInputId : undefined}
                title={hasSource ? undefined : 'Select a source first'}
                className={`inline-flex h-8 min-w-0 max-w-[min(100%,320px)] items-center rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-700 transition-colors ${
                  hasSource && !isRowUploading
                    ? 'cursor-pointer hover:bg-gray-50'
                    : 'cursor-not-allowed opacity-50 pointer-events-none'
                }`}
              >
                <span className="truncate">
                  {row.file ? row.file.name : 'Choose file'}
                </span>
              </label>
              <Button
                type="button"
                size="sm"
                variant="black"
                className="h-8 min-w-[88px] text-xs font-medium text-white hover:bg-gray-900"
                onClick={() => uploadOne(index)}
                disabled={!canUploadRow}
              >
                {isRowUploading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Uploading…
                  </span>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {uploadError && (
        <div className="flex items-center gap-2 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {uploadError}
        </div>
      )}

      {isRunningValidation && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-500" />
          Running validation against uploaded files…
        </div>
      )}

      {/* Uploaded files: show each file as a record */}
      {validationFiles.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            Uploaded files
          </h4>
          <div className="flex flex-wrap gap-2">
            {validationFiles.map((f, i) => {
              const fileSource = getValidationFileSource(f);
              const fileKey = fileSource ? validationFileKey(fileSource, f.filename) : f.filename;
              const isUploading = fileSource
                ? uploadingFileKeys.has(validationFileKey(fileSource, f.filename))
                : false;
              const isDeleting = fileSource
                ? deletingFileKeys.has(validationFileKey(fileSource, f.filename))
                : false;

              return (
                <ValidationFileChip
                  key={`${fileKey}-${i}`}
                  file={f}
                  isUploading={isUploading}
                  isDeleting={isDeleting}
                  onRemove={
                    fileSource
                      ? () => handleRemoveValidationFile(fileSource, f.filename)
                      : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Validation errors (from invoice group top-level errors) */}
      {validationErrors.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            Validation errors
          </h4>
          <ul className="text-xs text-red-600 space-y-1 list-disc list-inside max-h-32 overflow-y-auto">
            {validationErrors.map((msg, i) => (
              <li key={i}>
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Run validation button */}
      <div className="pt-2 border-t border-gray-200">
        <Button
          type="button"
          size="sm"
          variant="black"
          className="text-white hover:bg-gray-900"
          onClick={handleRunValidation}
          disabled={!hasUploadedFiles || isRunningValidation || hasUploadInProgress}
        >
          {isRunningValidation ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Running validation…
            </span>
          ) : (
            'Run validation'
          )}
        </Button>
        {!hasUploadedFiles && (
          <span className="ml-2 text-xs text-gray-500">
            Upload at least one file to run validation.
          </span>
        )}
        {hasUploadInProgress && hasUploadedFiles && (
          <span className="ml-2 text-xs text-gray-500">
            Wait for uploads to finish before running validation.
          </span>
        )}
      </div>
    </div>
  );
};

export default ValidateUploadContainer;
